'use client';

import { AlertCircle, ArrowLeft } from 'lucide-react';
import Link from 'next/link';
import React from 'react';

import { WikiRepositoryNotFound } from './wiki-repository-not-found';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { captureException } from '@/lib/sentry';

export type WikiViewVariant = 'public' | 'private';

type WikiErrorBoundaryProps = {
  children: React.ReactNode;
  resetKey?: string; // Key to trigger error reset on navigation
  variant?: WikiViewVariant; // Controls the UI style
  fallback?: React.ReactNode; // Custom fallback UI
};

type WikiErrorBoundaryState = {
  hasError: boolean;
  error?: Error;
};

export class WikiErrorBoundary extends React.Component<
  WikiErrorBoundaryProps,
  WikiErrorBoundaryState
> {
  constructor(props: WikiErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  componentDidUpdate(prevProps: WikiErrorBoundaryProps) {
    // Reset error state when resetKey changes (e.g., on navigation)
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false, error: undefined });
    }
  }

  static getDerivedStateFromError(error: Error): WikiErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Capture error with Sentry for proper error tracking
    captureException(error, {
      tags: {
        component: 'WikiErrorBoundary',
        variant: this.props.variant || 'private',
      },
      extra: {
        componentStack: errorInfo.componentStack,
        resetKey: this.props.resetKey,
        errorName: error.name,
      },
    });

    // Also log to console for development
    if (process.env.NODE_ENV === 'development') {
      console.error('[WikiErrorBoundary] Component error caught', {
        error,
        errorInfo,
        variant: this.props.variant || 'private',
        resetKey: this.props.resetKey,
      });
    }
  }

  renderPublicError() {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="mb-8">
          <Button variant="ghost" size="sm" asChild>
            <Link href="/wikis">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to directory
            </Link>
          </Button>
        </div>

        <Card className="mx-auto max-w-2xl p-12 text-center">
          <AlertCircle className="mx-auto mb-4 h-12 w-12 text-destructive" />
          <h2 className="mb-2 text-2xl font-semibold">Something went wrong</h2>
          <p className="mb-6 text-muted-foreground">
            We encountered an error while loading this wiki. Please try again later.
          </p>
          <div className="flex justify-center gap-4">
            <Button asChild>
              <Link href="/wikis">Browse public wikis</Link>
            </Button>
            <Button variant="outline" onClick={() => window.location.reload()}>
              Try again
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  renderPrivateError() {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] w-full">
        <WikiRepositoryNotFound message="The repository you're looking for doesn't exist or you don't have access to it." />
      </div>
    );
  }

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Otherwise use variant-specific UI
      if (this.props.variant === 'public') {
        return this.renderPublicError();
      }

      return this.renderPrivateError();
    }

    return this.props.children;
  }
}
